import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const EditProjectModal = ({ project, open, onClose, onSuccess, customers, engineers }) => {
  const [formData, setFormData] = useState({
    project_name: '',
    location: '',
    built_up_area: '',
    contract_value: '',
    start_date: '',
    expected_completion_date: '',
    assigned_engineers: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        project_name: project.project_name || '',
        location: project.location || '',
        built_up_area: project.built_up_area?.toString() || '',
        contract_value: project.contract_value?.toString() || '',
        start_date: project.start_date?.split('T')[0] || '',
        expected_completion_date: project.expected_completion_date?.split('T')[0] || '',
        assigned_engineers: project.assigned_engineers || []
      });
    }
  }, [project]);

  const toggleEngineer = (engineerId) => {
    setFormData(prev => ({
      ...prev,
      assigned_engineers: prev.assigned_engineers.includes(engineerId)
        ? prev.assigned_engineers.filter(id => id !== engineerId)
        : [...prev.assigned_engineers, engineerId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.assigned_engineers.length === 0) {
      toast.error('Please assign at least one engineer');
      return;
    }

    setSaving(true);

    try {
      await api.patch(`/projects/${project.project_id}`, {
        project_name: formData.project_name,
        location: formData.location,
        built_up_area: parseFloat(formData.built_up_area),
        contract_value: parseFloat(formData.contract_value),
        start_date: formData.start_date,
        expected_completion_date: formData.expected_completion_date,
        assigned_engineers: formData.assigned_engineers
      });

      toast.success('Project updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-stone-500" />
            Edit Project
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Update basic project information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2" data-testid="edit-project-form">
          {/* Project Name */}
          <div className="space-y-2">
            <Label>Project Name *</Label>
            <Input
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              placeholder="Villa Construction"
              required
              className="h-11 rounded-lg"
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label>Location *</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Whitefield, Bangalore"
              required
              className="h-11 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Built-up Area */}
            <div className="space-y-2">
              <Label>Built-up Area (sq.ft) *</Label>
              <Input
                type="number"
                value={formData.built_up_area}
                onChange={(e) => setFormData({ ...formData, built_up_area: e.target.value })}
                required
                className="h-11 rounded-lg"
              />
            </div>

            {/* Contract Value */}
            <div className="space-y-2">
              <Label>Contract Value (₹) *</Label>
              <Input
                type="number"
                value={formData.contract_value}
                onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
                required
                className="h-11 rounded-lg"
              />
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
                className="h-11 rounded-lg"
              />
            </div>

            {/* Expected Completion */}
            <div className="space-y-2">
              <Label>Expected Completion *</Label>
              <Input
                type="date"
                value={formData.expected_completion_date}
                onChange={(e) => setFormData({ ...formData, expected_completion_date: e.target.value })}
                required
                className="h-11 rounded-lg"
              />
            </div>
          </div>

          {/* Engineer Assignment */}
          <div className="space-y-3 p-4 bg-stone-50 border border-stone-200 rounded-lg">
            <Label className="flex items-center text-sm font-medium">
              <Users className="h-4 w-4 mr-2 text-stone-500" />
              Assigned Engineers *
            </Label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {engineers.map(engineer => (
                <label 
                  key={engineer.user_id} 
                  className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                    formData.assigned_engineers.includes(engineer.user_id)
                      ? 'bg-stone-900 border-stone-900 text-white'
                      : 'bg-white border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <Checkbox
                    checked={formData.assigned_engineers.includes(engineer.user_id)}
                    onCheckedChange={() => toggleEngineer(engineer.user_id)}
                    className="border-stone-400"
                  />
                  <span>{engineer.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11 rounded-lg"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 bg-stone-900 hover:bg-stone-800 text-white rounded-lg"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProjectModal;
